import { memo, useCallback, type ChangeEvent, type FC } from "react";
import { Link, Reply } from "lucide-react";
import { useTranslation } from "react-i18next";
import StyledTextInput from "src/components/reusable/StyledTextInput";
import CampaignFormField from "../../../shared/CampaignFormField";
import { WHATSAPP_EDITOR_KEY } from "../WhatsAppCampaignConstants";
import type {
  WhatsAppTemplateButton,
  WhatsAppVariableDraft,
  WhatsAppVariableDrafts,
} from "../WhatsAppCampaignTypes";
import { getButtonFixedUrl } from "../WhatsAppCampaignUtils";

const BUTTONS_KEY = `${WHATSAPP_EDITOR_KEY}.buttons`;
const OPT_OUT_KIND = "quick_opt_out";

interface ActionButtonsConfigProps {
  buttons: WhatsAppTemplateButton[];
  drafts: WhatsAppVariableDrafts;
  onDraftChange: (_key: string, _draft: Partial<WhatsAppVariableDraft>) => void;
}

const ActionButtonsConfig: FC<ActionButtonsConfigProps> = ({ buttons, drafts, onDraftChange }) => {
  const { t } = useTranslation();

  const handleSuffixChange = useCallback(
    (key: string) => (event: ChangeEvent<HTMLInputElement>) =>
      onDraftChange(key, { type: "static", value: event.target.value }),
    [onDraftChange]
  );

  if (!buttons.length) {
    return null;
  }

  return (
    <section className="wa-action-buttons">
      <h4 className="wa-action-buttons__title">{t(`${BUTTONS_KEY}.title`)}</h4>
      {buttons.map((button, position) => {
        const buttonNumber = position + 1;
        const labelId = `wa-button-${buttonNumber}-label`;
        const isUrlButton = button.type === "URL";
        const Icon = isUrlButton ? Link : Reply;
        const kindName = t(`${WHATSAPP_EDITOR_KEY}.buttonKinds.${button.kind}`, {
          defaultValue: button.kind,
        });

        return (
          <div key={`${button.type}-${buttonNumber}`} className="wa-action-buttons__item">
            <div className="wa-action-buttons__header">
              <span className="wa-action-buttons__name">
                <Icon className="wa-action-buttons__icon" aria-hidden />
                {t(`${BUTTONS_KEY}.name`, { number: buttonNumber, kind: kindName })}
              </span>
              {isUrlButton && (
                <span className="wa-parameter__tag wa-parameter__tag--pill">
                  {t(`${BUTTONS_KEY}.quickAction`)}
                </span>
              )}
            </div>

            {isUrlButton ? (
              <div className="wa-action-buttons__fields">
                <CampaignFormField label={t(`${BUTTONS_KEY}.url.label`)} htmlFor={labelId}>
                  <StyledTextInput
                    id={labelId}
                    value={button.text}
                    disabled
                    className="campaign-flow__input"
                  />
                </CampaignFormField>
                <CampaignFormField
                  label={t(`${BUTTONS_KEY}.url.destination`)}
                  htmlFor={`wa-button-${buttonNumber}-url`}
                >
                  {button.variableKey ? (
                    <StyledTextInput
                      id={`wa-button-${buttonNumber}-url`}
                      addonBefore={getButtonFixedUrl(button.url)}
                      value={drafts[button.variableKey]?.value ?? ""}
                      onChange={handleSuffixChange(button.variableKey)}
                      placeholder={t(`${BUTTONS_KEY}.url.suffixPlaceholder`)}
                      className="campaign-flow__input wa-action-buttons__url"
                    />
                  ) : (
                    <StyledTextInput
                      id={`wa-button-${buttonNumber}-url`}
                      value={button.url ?? ""}
                      disabled
                      className="campaign-flow__input wa-action-buttons__url"
                    />
                  )}
                </CampaignFormField>
              </div>
            ) : (
              <div className="wa-action-buttons__reply">
                <StyledTextInput
                  value={button.text}
                  disabled
                  aria-label={t(`${BUTTONS_KEY}.name`, { number: buttonNumber, kind: kindName })}
                  className="campaign-flow__input wa-action-buttons__reply-input"
                />
                {button.kind === OPT_OUT_KIND && (
                  <span className="wa-action-buttons__hint">{t(`${BUTTONS_KEY}.optOutHint`)}</span>
                )}
              </div>
            )}
          </div>
        );
      })}
    </section>
  );
};

export default memo(ActionButtonsConfig);
